import React, { useState, useEffect } from 'react';
import { GiOpenBook } from 'react-icons/gi';
import {
  LuFileText,
  LuEye,
  LuDownload,
  LuCalendar,
  LuBookOpen,
  LuBookmark,
  LuSparkles,
  LuLoader
} from 'react-icons/lu';
import publicationsHero from '../../assets/publication.png';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../shared/firebase';

export default function Readings({ activeSection = 'readings', onSelectSection = () => { } }) {
  const [readingsList, setReadingsList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch readings from Firestore
  useEffect(() => {
    let isMounted = true;
    async function fetchReadings() {
      try {
        setLoading(true);
        const querySnapshot = await getDocs(collection(db, 'readings'));
        const fetched = [];

        if (querySnapshot && !querySnapshot.empty) {
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            const title = data.title || 'Daily Bible Reading';
            const imgUrl = data.img_url || data.image || data.img || '';
            const pdfUrl = data.pdf_url || data.url || data.pdf || '';
            const order = typeof data.order === 'number' ? data.order : 999;

            const isAlmanac = title.toUpperCase().includes('ALMANAC');

            fetched.push({
              id: doc.id,
              title: title.toUpperCase(),
              subtitle: isAlmanac
                ? 'Official Liturgical Calendar & Lectionary'
                : 'Daily Scripture Portions & Meditation',
              year: data.year || (title.includes('2026') ? '2026 Edition' : 'Annual Edition'),
              description: data.description || (isAlmanac
                ? 'Comprehensive liturgical calendar containing daily Scripture portions, festival days, Sunday themes, liturgical colors, and diocesan prayer cycles.'
                : 'Structured daily morning and evening Bible reading plan designed for personal devotions, family prayers, and parish prayer fellowships.'),
              pdf_url: pdfUrl || '#',
              img_url: imgUrl,
              fileSize: data.filesize || 'Official PDF',
              order,
              raw: data
            });
          });

          fetched.sort((a, b) => a.order - b.order);
        }

        if (isMounted) {
          setReadingsList(fetched);
        }
      } catch (err) {
        console.error("Error fetching readings from Firebase:", err);
        if (isMounted) {
          setReadingsList([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchReadings();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#2D2420] font-sans pb-16">

      {/* ── 1. Standardized Hero Header Banner ── */}
      <section className="relative w-full overflow-hidden min-h-[380px] sm:min-h-[420px] lg:min-h-[440px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={publicationsHero}
            alt="CSI Readings and Liturgical Almanac"
            className="w-full h-full object-cover object-[right_center] filter brightness-[0.75] contrast-[1.08]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#140e1b]/90 via-[#140e1b]/40 to-black/30"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 sm:pt-32 pb-16 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
          
          <div className="max-w-xl text-left">
            <h1 className="font-serif font-bold text-white text-[38px] sm:text-[48px] lg:text-[56px] tracking-[0.04em] uppercase leading-[1.05] drop-shadow-md">
              READINGS &amp; ALMANAC
            </h1>
            <p className="text-[#E8B86D] text-[15px] sm:text-[17px] font-medium tracking-wide mt-2.5 drop-shadow-sm">
              Access daily Scripture reading portions, official diocesan almanacs, liturgical calendars, and devotional resources for your daily walk of faith.
            </p>
            <div className="flex items-center gap-2 text-xs text-white/80 font-medium mt-4">
              <span onClick={() => onSelectSection('home')} className="hover:text-white cursor-pointer transition-colors">Home</span>
              <span className="text-[#E8B86D] font-bold">›</span>
              <span onClick={() => onSelectSection('publications')} className="hover:text-white cursor-pointer transition-colors">Publications</span>
              <span className="text-[#E8B86D] font-bold">›</span>
              <span className="text-white font-semibold">Readings &amp; Almanac</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <LuCalendar className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">2026</span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Liturgical Almanac</span>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <LuBookOpen className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">365 Days</span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Daily Lectionary</span>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <GiOpenBook className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">Free</span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">PDF Downloads</span>
              </div>
            </div>
          </div>

        </div>

        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-10 sm:h-12 text-[#FAF7F2] fill-current">
            <path d="M0,0 C150,90 350,-40 500,60 C650,160 900,10 1200,40 L1200,120 L0,120 Z"></path>
          </svg>
        </div>
      </section>

      {/* ── 2. Readings Grid Cards ── */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mt-10 w-full">

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[24px] border border-gray-100 shadow-sm">
            <LuLoader className="w-10 h-10 text-[#842343] animate-spin mb-3" />
            <p className="text-sm font-semibold text-[#5A4F4A]">Connecting to Firebase & loading daily readings...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 items-stretch">
            {readingsList.map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-[28px] border border-[#E8DDD7] shadow-[0_4px_25px_rgba(0,0,0,0.03)] hover:shadow-[0_16px_40px_rgba(132,35,67,0.08)] hover:-translate-y-1 transition-all duration-300 p-6 sm:p-8 flex flex-col justify-between h-full relative overflow-hidden group"
              >
                <div>
                  {/* Top Bar with Icon / Cover Image */}
                  <div className="flex items-start justify-between gap-4 mb-5 relative z-10">
                    {item.img_url ? (
                      <div className="w-20 h-28 rounded-xl overflow-hidden shadow-md border border-gray-200 shrink-0 bg-gray-50">
                        <img
                          src={item.img_url}
                          alt={item.title}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                          }}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    ) : (
                      <div className="w-14 h-14 rounded-2xl bg-[#FAF6F2] border border-[#E8DDD7]/70 flex items-center justify-center text-[#842343] shrink-0 group-hover:bg-[#842343] group-hover:text-white transition-colors duration-300 shadow-xs">
                        <LuFileText className="w-7 h-7" />
                      </div>
                    )}

                    <div className="flex flex-col items-end gap-1">
                      <span className="text-[11px] font-bold uppercase tracking-wider px-3 py-1 bg-[#842343]/10 text-[#842343] rounded-full border border-[#842343]/20">
                        {item.year}
                      </span>
                      <span className="text-xs text-[#8A7E79] font-medium flex items-center gap-1">
                        <LuBookmark className="w-3.5 h-3.5" /> {item.fileSize}
                      </span>
                    </div>
                  </div>

                  <h2 className="font-serif font-bold text-[#1a1715] group-hover:text-[#842343] text-xl sm:text-2xl tracking-tight mb-1 transition-colors relative z-10">
                    {item.title}
                  </h2>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#D4A64A] mb-3 relative z-10">
                    {item.subtitle}
                  </p>
                  <p className="text-[#5A4F4A] text-xs sm:text-sm leading-relaxed mb-6 relative z-10">
                    {item.description}
                  </p>
                </div>

                {/* Bottom Action Links */}
                <div className="pt-4 border-t border-[#F0E6E0] flex flex-col sm:flex-row items-center gap-3 relative z-10 mt-auto">
                  {item.pdf_url && item.pdf_url !== '#' ? (
                    <>
                      <a
                        href={item.pdf_url}
                        target="_blank"
                        rel="noreferrer"
                        className="w-full sm:w-1/2 flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-[#842343] text-[#842343] font-bold text-xs hover:bg-[#842343]/10 transition-colors duration-300"
                      >
                        <LuEye className="w-4 h-4" />
                        <span>Preview Online</span>
                      </a>
                      <a
                        href={item.pdf_url}
                        target="_blank"
                        rel="noreferrer"
                        download
                        className="w-full sm:w-1/2 flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#842343] text-white font-bold text-xs hover:bg-[#6c1b35] shadow-xs hover:shadow-md transition-all duration-300"
                      >
                        <LuDownload className="w-4 h-4" />
                        <span>Download PDF</span>
                      </a>
                    </>
                  ) : (
                    <button
                      onClick={() => alert(`Official PDF for ${item.title} will be available for download shortly.`)}
                      className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#842343] text-white font-bold text-xs hover:bg-[#6c1b35] shadow-xs cursor-pointer"
                    >
                      <LuFileText className="w-4 h-4" />
                      <span>Request PDF Copy</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bottom Callout */}
        <div className="mt-10 bg-white rounded-[28px] p-6 sm:p-8 border border-[#E8DDD7] flex flex-col sm:flex-row items-center justify-between gap-6 shadow-[0_4px_25px_rgba(0,0,0,0.02)]">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#D4A64A]/15 text-[#D4A64A] flex items-center justify-center shrink-0">
              <LuSparkles className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-serif font-bold text-[#1a1715] text-[17px] sm:text-[18px]">
                Need Printed Hard Copies?
              </h3>
              <p className="text-[#5A4F4A] text-xs sm:text-[13px]">
                Official printed Diocesan Almanacs and daily lectionary books are available at the Diocesan Central Book Room &amp; Parishes.
              </p>
            </div>
          </div>
          <button
            onClick={() => onSelectSection('about')}
            className="shrink-0 bg-[#D4A64A] hover:bg-[#C49539] text-[#1a1715] font-bold text-xs sm:text-[13px] px-6 py-3 rounded-xl transition-colors cursor-pointer"
          >
            Contact Diocesan Office →
          </button>
        </div>
      </div>
    </div>
  );
}
